import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface UndoEntry {
  id: number;
  message: string;
  undo: () => void | Promise<void>;
}

interface UndoAPI {
  /** Show a toast offering to reverse what just happened. */
  offer: (message: string, undo: () => void | Promise<void>) => void;
  current: UndoEntry | null;
  run: () => void;
  dismiss: () => void;
}

const UndoContext = createContext<UndoAPI | null>(null);

/** Auto-dismiss window. Long enough to notice, short enough to stop nagging. */
const TTL = 5000;

export function UndoProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<UndoEntry | null>(null);
  const timer = useRef<number>(0);
  const seq = useRef(0);

  const clear = useCallback(() => {
    window.clearTimeout(timer.current);
    setCurrent(null);
  }, []);

  const offer = useCallback(
    (message: string, undo: () => void | Promise<void>) => {
      window.clearTimeout(timer.current);
      setCurrent({ id: ++seq.current, message, undo });
      timer.current = window.setTimeout(() => setCurrent(null), TTL);
    },
    [],
  );

  const run = useCallback(() => {
    setCurrent((entry) => {
      void entry?.undo();
      return null;
    });
    window.clearTimeout(timer.current);
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const value = useMemo(
    () => ({ offer, current, run, dismiss: clear }),
    [offer, current, run, clear],
  );
  return <UndoContext.Provider value={value}>{children}</UndoContext.Provider>;
}

export function useUndo() {
  const ctx = useContext(UndoContext);
  if (!ctx) throw new Error('useUndo must be used inside <UndoProvider>');
  return ctx;
}
